import { computed, onMounted, onUnmounted, ref } from "vue";

export interface PullToRefreshConfig {
  /** Minimum pull distance to trigger refresh */
  threshold?: number;
  /** Maximum pull distance allowed */
  maxPull?: number;
  /** Resistance factor (1 = no resistance, 2 = half distance, etc) */
  resistance?: number;
  /** Animation duration in ms */
  animationDuration?: number;
  /** Scale ratio for the icon */
  scaleRatio?: number;
}

const DEFAULT_CONFIG = {
  threshold: 100,
  maxPull: 200,
  resistance: 2,
  animationDuration: 300,
  scaleRatio: 2,
} as const;

export function usePullToRefresh(config: PullToRefreshConfig = {}) {
  const opts = { ...DEFAULT_CONFIG, ...config };

  const pullDistance = ref(0);
  const isDragging = ref(false);
  const isRefreshing = ref(false);

  let startY = 0;
  let rafId: number | null = null;

  const calculateResistance = (distance: number): number => {
    return distance / opts.resistance;
  };

  const progress = computed(() => {
    const ratio = Math.min(pullDistance.value / opts.threshold, 1);
    const scale = 1 + (pullDistance.value / opts.maxPull) * opts.scaleRatio; // 60% max scale increase

    return {
      rotation: `rotate(${ratio * 360}deg)`,
      opacity: Math.min(ratio * 1.5, 1),
      scale: scale,
      canRefresh: ratio >= 1,
      shouldShow: ratio >= 0.3,
    };
  });

  const animateReset = () => {
    const startDistance = pullDistance.value;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / opts.animationDuration, 1);

      // Easing function for smooth animation
      const eased = 1 - Math.pow(1 - progress, 3);
      pullDistance.value = startDistance * (1 - eased);

      if (progress < 1) {
        rafId = requestAnimationFrame(animate);
      } else {
        pullDistance.value = 0;
        rafId = null;
      }
    };

    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(animate);
  };

  const handleTouchStart = (e: TouchEvent) => {
    if (window.scrollY > 0 || isRefreshing.value) return;

    isDragging.value = true;
    startY = e.touches[0].clientY;

    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging.value) return;

    const y = e.touches[0].clientY;
    const delta = y - startY;

    if (delta > 0) {
      const resistance = calculateResistance(delta);
      pullDistance.value = Math.min(resistance, opts.maxPull);

      if (e.cancelable) {
        e.preventDefault();
      }
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging.value) return;

    isDragging.value = false;

    if (pullDistance.value >= opts.threshold) {
      isRefreshing.value = true;
      window.location.reload();
    } else {
      animateReset();
    }
  };

  onMounted(() => {
    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);
  });

  onUnmounted(() => {
    document.removeEventListener("touchstart", handleTouchStart);
    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("touchend", handleTouchEnd);

    if (rafId) cancelAnimationFrame(rafId);
  });

  return {
    pullDistance,
    isDragging,
    isRefreshing,
    rotation: computed(() => progress.value.rotation),
    opacity: computed(() => progress.value.opacity),
    scale: computed(() => progress.value.scale),
    canRefresh: computed(() => progress.value.canRefresh),
    showIcon: computed(() => progress.value.shouldShow),
  };
}
